<template>
  <div class="multiple-image-uploader">
    <el-upload
      ref="uploadRef"
      class="image-uploader"
      :http-request="customUpload"
      :before-upload="beforeImageUpload"
      :show-file-list="false"
      accept="image/jpeg,image/png,image/gif"
      multiple
    >
      <el-button type="primary">
        <el-icon><Plus /></el-icon>上传图片
      </el-button>
      <div class="tip-text">支持多图上传</div>
    </el-upload>

    <div v-if="imageUrls.length > 0" class="image-list">
      <div v-for="(url, index) in imageUrls" :key="index" class="image-item">
        <el-image
          :src="url"
          fit="cover"
          :preview-src-list="imageUrls"
          :initial-index="index"
        >
          <template #error>
            <div class="image-error">
              <el-icon><Picture /></el-icon>
            </div>
          </template>
        </el-image>
        <div class="image-actions">
          <el-button
            type="danger"
            size="small"
            circle
            @click="removeImage(index)"
          >
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { uploadImage, beforeImageUpload } from "@/api/uploadImage"
import { ElMessage } from "element-plus"
import { Plus, Delete, Picture } from "@element-plus/icons-vue"

const props = defineProps<{
  modelValue?: string[] // 初始图片列表
}>()

const emit = defineEmits<{
  "update:modelValue": [value: string[]]
  change: [value: string[]]
}>()

const uploadRef = ref()
const imageUrls = ref<string[]>(props.modelValue || [])

// 监听props.modelValue的变化
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      imageUrls.value = newVal
    }
  }
)

// 监听imageUrls的变化，更新父组件
watch(
  imageUrls,
  (newVal) => {
    emit("update:modelValue", newVal)
    emit("change", newVal)
  },
  { deep: true }
)

// 自定义上传方法
const customUpload = async (options: any) => {
  try {
    const imageUrl = await uploadImage(options.file)
    if (imageUrl) {
      imageUrls.value.push(imageUrl)
      ElMessage.success("上传成功")
    }
  } catch (error) {
    console.error("上传失败:", error)
    ElMessage.error("上传失败，请重试")
  }
}

// 删除图片
const removeImage = (index: number) => {
  imageUrls.value.splice(index, 1)
}

// 清空所有图片
const clearFiles = () => {
  imageUrls.value = []
  if (uploadRef.value) {
    uploadRef.value.clearFiles()
  }
}

// 暴露方法给父组件
defineExpose({
  clearFiles,
  imageUrls
})
</script>

<style scoped>
.multiple-image-uploader {
  width: 100%;
}

.tip-text {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.image-item .el-image {
  width: 100%;
  height: 100%;
  display: block;
}

.image-actions {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 0 4px;
  padding: 2px;
  display: none;
}

.image-item:hover .image-actions {
  display: block;
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
}
</style>
